import {List, Map} from 'immutable';
import {javaApiFor, triggersApi} from 'sources/api';
import DepartmentWithTeams from 'records/admin/topics/DepartmentWithTeams';
import NewFilterWordProfilesInAccount from 'records/admin/topics/NewFilterWordProfilesInAccount';
import NewFilterWordProfile from 'records/admin/topics/NewFilterWordProfile';
import NewFilterWord from 'records/admin/topics/NewFilterWord';
import TriggerRuleInfo from 'records/TriggerRuleInfo';

const departmentsApi = javaApiFor('/profile/departments');
const filterWordsApi = javaApiFor('/profile/filter_words');
const filterWordProfilesApi = javaApiFor('/profile/filter_words/profiles');

export default class AdminFilterWordProfileSource {
  static associateProfileWithDepartment(filterWordProfileId, departmentId) {
    return filterWordProfilesApi.postJson(`/${filterWordProfileId}/department/${departmentId}`);
  }

  static associateProfileWithTeam(filterWordProfileId, teamId) {
    return filterWordProfilesApi.postJson(`/${filterWordProfileId}/team/${teamId}`);
  }

  static cloneFilterWord(filterWordId) {
    return filterWordsApi
      .postJson(`/${filterWordId}/clone`)
      .then(filterWord => new NewFilterWord(filterWord));
  }

  static createFilterWord(word) {
    return filterWordsApi.postJson('/', word).then(filterWord => new NewFilterWord(filterWord));
  }

  static createProfile(profile, {departmentId, teamId}) {
    const payload = {
      profile: profile.sanitizeForServer().toJS(),
      departmentId,
      teamId,
    };
    return filterWordProfilesApi
      .postJson('/', payload)
      .then(profile => new NewFilterWordProfile(profile));
  }

  static deleteFilterWord(filterWordId) {
    return filterWordsApi.delete(`/${filterWordId}`);
  }

  static dissociateDepartmentProfile(departmentId) {
    return filterWordProfilesApi.delete(`/department/${departmentId}`);
  }

  static dissociateTeamProfile(teamId) {
    return filterWordProfilesApi.delete(`/team/${teamId}`);
  }

  static fetchAccountProfiles() {
    return filterWordProfilesApi
      .getJson('/')
      .then(profilesInAccount => new NewFilterWordProfilesInAccount(profilesInAccount));
  }

  static fetchDepartments() {
    return departmentsApi
      .getJson('/')
      .then(departments =>
        List(departments.map(department => new DepartmentWithTeams(department)))
      );
  }

  // No action or store changes for this.
  static fetchProfileByTriggerRuleUsage(triggerRuleIds) {
    return (
      filterWordProfilesApi
        .postJson('/by_triggers', triggerRuleIds)
        // Note: because the rule IDs are keys in the object 'map' here, they're strings. The
        // server sends them that way, having serialized a JSON object originally being a map with
        // integer keys. Possibly confusing, but it works out nicely in our case, because the keys
        // we're using for lookup have ALSO (for unrelated reasons) been converted from integers to
        // strings.
        .then(map => Map(map).map(array => List(array)))
    );
  }

  // No action or store changes for this.
  static fetchTopic(filterWordId) {
    return filterWordsApi.getJson(`/${filterWordId}`).then(resp => new NewFilterWord(resp));
  }

  static fetchTriggers(filterWordId) {
    return triggersApi
      .getJson(`/filter_word_usage/triggers/${filterWordId}`)
      .then(rules =>
        List(
          rules.sort((a, b) => a.name.localeCompare(b.name)).map(rule => new TriggerRuleInfo(rule))
        )
      );
  }

  static mergeTopics([topicsToMerge, mergedName]) {
    // We're going to arbitrarily select the first in alphabetical order to merge others into.
    const asList = List(topicsToMerge).sortBy(topic => topic.word);
    const [firstId, ...topicsToMergeIntoFirst] = asList.map(fw => fw.filterWordId);
    return filterWordsApi
      .postJson(`/${firstId}/merge`, {
        mergeIds: topicsToMergeIntoFirst,
        mergedName: mergedName,
      })
      .then(() => [firstId, mergedName]);
  }

  static updateFilterWord(filterWord) {
    return filterWordsApi
      .putJson(`/${filterWord.filterWordId}`, filterWord.toJS())
      .then(updated => new NewFilterWord(updated));
  }

  static updateProfile(profile) {
    return filterWordProfilesApi
      .putJson(`/${profile.filterWordProfileId}`, profile.sanitizeForServer().toJS())
      .then(updated => new NewFilterWordProfile(updated));
  }
}
